import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, database } from './firebase';
import { ref, get, onValue } from 'firebase/database';
import { useImpersonation } from './ImpersonationContext';

const FUNCTIONS_BASE = 'https://us-central1-cue-clock-pro.cloudfunctions.net';

async function callFunction(name, body) {
  const token = await auth.currentUser.getIdToken(true);
  const resp = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${resp.status})`);
  }
  return resp.json();
}

// ─── Grade constants ──────────────────────────────────────────────────────────

const GRADE_TIERS = ['A', 'B', 'C', 'D', 'E'];
const GRADE_LEVELS = ['High', 'Mid', 'Low'];
const ALL_GRADES = GRADE_TIERS.flatMap(t => GRADE_LEVELS.map(l => `${l} ${t}`));

const GRADE_COLOURS = {
  A: '#f59e0b',
  B: '#10b981',
  C: '#3b82f6',
  D: '#8b5cf6',
  E: '#6b7280',
  Open: '#374151',
};

const FINISH_RANK = {
  'Winner': 6, 'Runner-up': 5, 'Semi-finalist': 4,
  'Quarter-finalist': 3, 'Last 16': 2, 'Group/Early': 1,
};

function gradeColour(grade) {
  if (!grade) return '#374151';
  const tier = grade.replace(/^(High|Mid|Low)\s+/, '');
  return GRADE_COLOURS[tier] || '#374151';
}

function finishEmoji(finish) {
  if (finish === 'Winner') return '🏆';
  if (finish === 'Runner-up') return '🥈';
  if (finish === 'Semi-finalist') return '🥉';
  if (finish === 'Quarter-finalist') return '4️⃣';
  return '▪️';
}

// ─── Suggestion distance helper (how far off is assigned from suggestion) ────
function gradeSuggestionWarning(assignedGrade, suggestion) {
  if (!suggestion || !assignedGrade) return null;
  const TIER_ORDER = { E: 0, D: 1, C: 2, B: 3, A: 4 };
  const LEVEL_ORDER = { Low: 0, Mid: 1, High: 2 };

  const [assignedLevel, assignedTier] = assignedGrade.split(' ');
  const sugTier = suggestion.tier;
  const sugLevel = suggestion.level;

  if (!assignedTier || !assignedLevel) return null;

  const tierDiff = TIER_ORDER[assignedTier] - TIER_ORDER[sugTier];
  const levelDiff = LEVEL_ORDER[assignedLevel] - LEVEL_ORDER[sugLevel];
  const totalDiff = tierDiff * 3 + levelDiff;

  if (Math.abs(totalDiff) >= 3) {
    const direction = totalDiff > 0 ? 'higher' : 'lower';
    return `This grade is significantly ${direction} than the data suggests (${suggestion.grade}). Are you sure?`;
  }
  return null;
}

// ─── CSV export ───────────────────────────────────────────────────────────────
function exportToCSV(players, savedGrades) {
  const headers = [
    'Player', 'Assigned Grade', 'Suggested Grade', 'Tournaments', 'Matches Played',
    'Won', 'Lost', 'Win %', 'Frames Won', 'Frames Lost', 'Frame Ratio',
    'Best Finish', 'Tournament Wins', 'Finals', 'Semis', 'Notes',
  ];

  const rows = players.map(p => {
    const saved = savedGrades[p.playerId];
    return [
      p.playerName,
      saved ? saved.grade : '',
      p.suggestion ? p.suggestion.grade : 'Insufficient data',
      p.tournamentsEntered,
      p.matchesPlayed,
      p.matchesWon,
      p.matchesLost,
      `${p.winRate}%`,
      p.framesWon,
      p.framesLost,
      p.frameRatio,
      p.bestFinish,
      p.tournamentWins,
      p.finalAppearances,
      p.semiAppearances,
      saved ? (saved.notes || '') : '',
    ];
  });

  const csvContent = [headers, ...rows]
    .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `player-grades-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main component ───────────────────────────────────────────────────────────
function PlayerGrading() {
  const navigate = useNavigate();
  const { effectiveUserId } = useImpersonation();

  const [user, setUser] = useState(null);
  const [players, setPlayers] = useState([]);
  const [savedGrades, setSavedGrades] = useState({});
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null); // { added, skipped, failed, total }
  const [historyMeta, setHistoryMeta] = useState(null); // from Firebase tournamentHistoryMeta
  const [saving, setSaving] = useState(null); // playerId being saved
  const [error, setError] = useState('');
  const [lastGenerated, setLastGenerated] = useState(null);
  const [tournamentsProcessed, setTournamentsProcessed] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('All');
  const [filterUngraded, setFilterUngraded] = useState(false);
  const [filterFlags, setFilterFlags] = useState(false);
  const [sortBy, setSortBy] = useState('winRate');
  const [editingGrade, setEditingGrade] = useState(null); // { playerId, grade, notes }
  const [confirmWarning, setConfirmWarning] = useState(null); // pending save needing confirmation
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [cuescoreInput, setCuescoreInput] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [syncingPlayers, setSyncingPlayers] = useState(false);
  const [syncAllProgress, setSyncAllProgress] = useState(null);
  const syncAllCancelRef = React.useRef(false);
  const [rebuttalModal, setRebuttalModal] = useState(null);
  const [rebuttalNote, setRebuttalNote] = useState('');
  const [expandedRationale, setExpandedRationale] = useState(null);
  const [playersExpanded, setPlayersExpanded] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const isMobile = window.innerWidth < 768;

  // ── Auth guard — also auto-load grading data once authenticated ──
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => {
      if (u) {
        setUser(u);
        // Auto-load grading stats on page load so the player list shows immediately
        // without requiring the user to click Compute Grading Stats manually.
        fetchGradingData();
      } else {
        navigate('/organiser-login');
      }
    });
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // ── Pre-load CueScore username ──
  useEffect(() => {
    if (!effectiveUserId) return;
    get(ref(database, `organisers/${effectiveUserId}/cuescoreUsername`)).then(snap => {
      if (snap.exists() && snap.val()) setCuescoreInput(snap.val());
    });
  }, [effectiveUserId]);

  // ── Load saved grades from Firebase ──
  useEffect(() => {
    if (!effectiveUserId) return;
    const gradesRef = ref(database, `organisers/${effectiveUserId}/playerGrades`);
    const unsub = onValue(gradesRef, snap => {
      setSavedGrades(snap.exists() ? snap.val() : {});
    });
    return unsub;
  }, [effectiveUserId]);

  // ── Load tournament history meta from Firebase ──
  useEffect(() => {
    if (!effectiveUserId) return;
    const metaRef = ref(database, `organisers/${effectiveUserId}/tournamentHistoryMeta`);
    const unsub = onValue(metaRef, snap => {
      setHistoryMeta(snap.exists() ? snap.val() : null);
    });
    return unsub;
  }, [effectiveUserId]);

  // ── Sync tournament history from CueScore → Firebase ──
  // Auto-advances through pages (one page per call) until the server returns
  // nextPage: null (empty page = end of tournament list). Each page processes
  // ~20 tournaments and completes well within the 60-second function limit.
  const handleSyncHistory = async (resync = false) => {
    if (!auth.currentUser) {
      setError('You must be logged in. Please refresh the page and try again.');
      return;
    }
    if (!cuescoreInput.trim()) {
      setError('Please enter your CueScore public username first.');
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    setError('');
    let page = 1;
    let totalAdded = 0;
    let totalSkipped = 0;
    let totalFailed = 0;
    try {
      while (true) {
        console.log(`[Sync] Page ${page}...`);
        const result = await callFunction('syncTournamentHistory', {
          resync,
          cuescoreUsername: cuescoreInput.trim(),
          page,
        });
        totalAdded += result.added || 0;
        totalSkipped += result.skipped || 0;
        totalFailed += result.failed || 0;
        console.log(`[Sync] Page ${page} done — added=${result.added} skipped=${result.skipped} nextPage=${result.nextPage}`);
        setSyncResult({ added: totalAdded, skipped: totalSkipped, failed: totalFailed, total: result.total });
        if (!result.nextPage) break;
        page = result.nextPage;
      }
      if (totalAdded > 0 || players.length === 0) {
        await fetchGradingData();
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(`Sync failed on page ${page}: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // ── Load grading stats from stored history ──
  const fetchGradingData = async () => {
    setLoading(true);
    setError('');
    setPlayers([]);
    setExpandedPlayer(null);
    setEditingGrade(null);
    setCurrentPage(1);
    try {
      const result = await callFunction('getPlayerGradingData', {});
      const { players: fetchedPlayers, tournamentsProcessed: tp, generatedAt } = result;
      setPlayers(fetchedPlayers || []);
      setTournamentsProcessed(tp || 0);
      setLastGenerated(generatedAt ? new Date(generatedAt) : new Date());
    } catch (err) {
      console.error('Grading fetch error:', err);
      setError(`Failed to load grading data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Save a grade (with optional rebuttal note) ──
  const handleSaveGrade = async (playerId, grade, notes, skipWarning = false, rebuttalNoteArg = null) => {
    const player = players.find(p => p.playerId === playerId);
    if (!player) return;

    // Check for significant deviation warning (algorithm confidence)
    if (!skipWarning && player.suggestion) {
      const warning = gradeSuggestionWarning(grade, player.suggestion);
      if (warning) {
        setConfirmWarning({ playerId, grade, notes, warning, playerName: player.playerName });
        return;
      }
    }

    // If tier differs from suggestion and rebuttal hasn't been handled yet,
    // prompt organiser for an optional explanation before saving
    const suggestedTier = player.suggestion && player.suggestion.tier;
    const assignedTier = grade.includes(' ') ? grade.split(' ')[1] : grade;
    if (
      !skipWarning &&
      rebuttalNoteArg === null &&
      suggestedTier &&
      assignedTier !== suggestedTier
    ) {
      setRebuttalModal({ playerId, grade, notes, playerName: player.playerName,
        suggestion: player.suggestion });
      setRebuttalNote('');
      setEditingGrade(null);
      return;
    }

    setSaving(playerId);
    setEditingGrade(null);
    setConfirmWarning(null);
    setRebuttalModal(null);
    try {
      await callFunction('savePlayerGrade', {
        playerId,
        playerName: player.playerName,
        grade,
        notes: notes || '',
        systemSuggestion: player.suggestion || null,
        rebuttalNote: rebuttalNoteArg || '',
      });
    } catch (err) {
      console.error('Save grade error:', err);
      setError(`Failed to save grade: ${err.message}`);
    } finally {
      setSaving(null);
    }
  };

  // Called from the rebuttal modal — saves with whatever note the organiser typed
  const handleSaveFromRebuttal = () => {
    if (!rebuttalModal) return;
    handleSaveGrade(
      rebuttalModal.playerId, rebuttalModal.grade,
      rebuttalModal.notes, true, rebuttalNote
    );
  };

  // ── Sync all graded players — batches of 3, auto-advances via cursor ──
  const handleSyncAll = async () => {
    if (!auth.currentUser) { setError('You must be logged in.'); return; }
    syncAllCancelRef.current = false;
    setSyncAllProgress({ processed: 0, total: null, added: 0, running: true });
    let cursor = null;
    let totalAdded = 0;
    let totalProcessed = 0;
    let batchNum = 0;
    while (!syncAllCancelRef.current) {
      batchNum++;
      console.log(`[SyncAll] Batch ${batchNum} — cursor=${cursor || 'start'}`);
      try {
        const body = { maxPlayers: 3 };
        if (cursor) body.startAfter = cursor;
        const result = await callFunction('syncPlayerGradedHistory', body);
        totalProcessed += result.playersProcessed || 0;
        totalAdded += result.added || 0;
        console.log(`[SyncAll] Batch ${batchNum} done — players=${result.playersProcessed} added=${result.added} total=${result.totalPlayers} next=${result.nextPlayerId || 'DONE'}`);
        if (result.debug) result.debug.forEach(line => console.log('[SyncAll]', line));
        setSyncAllProgress({
          processed: totalProcessed,
          total: result.totalPlayers || null,
          added: totalAdded,
          running: !!result.nextPlayerId,
        });
        if (result.added > 0) fetchGradingData();
        if (!result.nextPlayerId) { console.log('[SyncAll] All players processed'); break; }
        cursor = result.nextPlayerId;
      } catch (err) {
        console.error('[SyncAll] Error:', err);
        setError(`Sync all failed: ${err.message}`);
        break;
      }
    }
    setSyncAllProgress(p => p ? { ...p, running: false } : null);
  };

  // ── Sync graded tournaments from individual player CueScore profiles ──
  const handleSyncPlayerHistory = async (targetPlayerId = '') => {
    if (!auth.currentUser) { setError('You must be logged in.'); return; }
    console.log('[Sync]', targetPlayerId ? `Syncing player ${targetPlayerId}` : 'Syncing all graded players');
    setSyncingPlayers(true);
    setError('');
    try {
      const body = targetPlayerId ? {targetPlayerId: String(targetPlayerId)} : {};
      const result = await callFunction('syncPlayerGradedHistory', body);
      console.log(`[Sync] added=${result.added} skipped=${result.skipped} failed=${result.failed} players=${result.playersProcessed}`);
      if (result.debug) result.debug.forEach(line => console.log('[Sync]', line));
      if (result.added > 0) {
        console.log('[Sync] New tournaments added — recomputing stats');
        await fetchGradingData();
      }
    } catch (err) {
      console.error('Player sync error:', err);
      setError(`Player history sync failed: ${err.message}`);
    } finally {
      setSyncingPlayers(false);
    }
  };

  // ── Seed baseline grades ──
  const handleSeedGrades = async () => {
    if (!auth.currentUser) { setError('You must be logged in.'); return; }
    setSeeding(true);
    setError('');
    setSeedResult(null);
    try {
      const result = await callFunction('seedBaselineGrades', {});
      setSeedResult(result);
      if (result.seeded > 0) await fetchGradingData();
    } catch (err) {
      console.error('Seed error:', err);
      setError(`Seed failed: ${err.message}`);
    } finally {
      setSeeding(false);
    }
  };

  // ── Filtered and sorted players ──
  const displayPlayers = useMemo(() => {
    let result = [...players];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.playerName.toLowerCase().includes(q));
    }

    if (filterTier !== 'All') {
      if (filterTier === 'Open') {
        result = result.filter(p => !p.suggestion || p.suggestion.tier === 'Open');
      } else {
        result = result.filter(p => p.suggestion && p.suggestion.tier === filterTier);
      }
    }

    if (filterUngraded) {
      result = result.filter(p => !savedGrades[p.playerId]);
    }

    if (filterFlags) {
      result = result.filter(p => p.flags && p.flags.length > 0);
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'winRate': return b.winRate - a.winRate;
        case 'matches': return b.matchesPlayed - a.matchesPlayed;
        case 'name': return a.playerName.localeCompare(b.playerName);
        case 'grade': {
          const TIER = { A: 4, B: 3, C: 2, D: 1, E: 0 };
          const LEVEL = { High: 2, Mid: 1, Low: 0 };
          const gradeParts = g => {
            if (!g) return [-1, -1];
            const [level, tier] = g.split(' ');
            return [TIER[tier] ?? -1, LEVEL[level] ?? -1];
          };
          const [aTier, aLevel] = gradeParts(savedGrades[a.playerId]?.grade);
          const [bTier, bLevel] = gradeParts(savedGrades[b.playerId]?.grade);
          return bTier !== aTier ? bTier - aTier : bLevel - aLevel;
        }
        default: return 0;
      }
    });

    return result;
  }, [players, searchQuery, filterTier, filterUngraded, filterFlags, sortBy, savedGrades]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#0d1321',
      color: '#f0f2f5',
      padding: isMobile ? '16px' : '24px',
      fontFamily: "'DM Sans', sans-serif",
    },
    header: {
      maxWidth: '1400px',
      margin: '0 auto 32px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '16px',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
    },
    content: { maxWidth: '1400px', margin: '0 auto' },
    card: {
      backgroundColor: '#151c2e',
      borderRadius: '14px',
      padding: isMobile ? '16px' : '24px',
      marginBottom: '20px',
      border: '1px solid #2a3555',
    },
    cardTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: 'bold',
      marginBottom: '16px',
      fontFamily: "'Outfit', sans-serif",
    },
    btn: (colour = '#3b82f6', small = false) => ({
      padding: small ? '6px 12px' : '10px 18px',
      backgroundColor: colour,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: small ? '13px' : '14px',
      fontWeight: 'bold',
    }),
    outlineBtn: (small = false) => ({
      padding: small ? '6px 12px' : '10px 18px',
      backgroundColor: '#1c2640',
      color: '#8b95a8',
      border: '1px solid #2a3555',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: small ? '13px' : '14px',
    }),
    badge: (colour) => ({
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '6px',
      backgroundColor: colour + '22',
      color: colour,
      fontSize: '13px',
      fontWeight: 'bold',
      border: `1px solid ${colour}44`,
    }),
    input: {
      padding: '8px 12px',
      backgroundColor: '#1c2640',
      color: '#f0f2f5',
      border: '1px solid #2a3555',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
    },
    select: {
      padding: '8px 12px',
      backgroundColor: '#1c2640',
      color: '#f0f2f5',
      border: '1px solid #2a3555',
      borderRadius: '8px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    playerRow: {
      backgroundColor: '#1c2640',
      borderRadius: '10px',
      padding: '16px',
      marginBottom: '10px',
      border: '1px solid #2a3555',
    },
    statPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 10px',
      backgroundColor: '#0d1321',
      borderRadius: '6px',
      fontSize: '13px',
      color: '#8b95a8',
    },
  };

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', marginTop: '100px', color: '#8b95a8' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            <img src="/icon.png" alt="Cue Clock Pro" style={{ width: '40px', height: '40px', borderRadius: '10px' }} />
            <div>
              <div style={{ fontSize: isMobile ? '20px' : '26px', fontWeight: '700', fontFamily: "'Outfit', sans-serif" }}>
                Player Grading
              </div>
              <div style={{ fontSize: '13px', color: '#8b95a8' }}>Cue Clock Pro — Organiser Tool</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/organiser-dashboard')} style={styles.outlineBtn()}>
            ◀ Dashboard
          </button>
          {players.length > 0 && (
            <button onClick={() => exportToCSV(players, savedGrades)} style={styles.outlineBtn()}>
              📥 Export CSV
            </button>
          )}
        </div>
      </div>

      <div style={styles.content}>
        {/* Tournament History Sync Panel */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>🗄️ Tournament History Store</h2>
          <p style={{ color: '#8b95a8', marginBottom: '16px', fontSize: '14px' }}>
            Step 1 — Sync all completed CueScore tournaments into Firebase. Only new tournaments are fetched each time. Run this whenever you've run new events.
          </p>

          {/* Meta info from Firebase */}
          {historyMeta && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#0d1321',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #2a3555',
              display: 'flex',
              gap: '24px',
              flexWrap: 'wrap',
              fontSize: '14px',
            }}>
              <span>📦 <strong style={{ color: '#f0f2f5' }}>{historyMeta.totalStored}</strong> <span style={{ color: '#8b95a8' }}>tournaments stored</span></span>
              <span>🕐 <span style={{ color: '#8b95a8' }}>Last sync:</span> <strong style={{ color: '#f0f2f5' }}>{new Date(historyMeta.lastSync).toLocaleString()}</strong></span>
            </div>
          )}

          {/* Sync result banner */}
          {syncResult && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: 'rgba(16,185,129,0.1)',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid rgba(16,185,129,0.3)',
              fontSize: '14px',
              color: '#10b981',
            }}>
              ✅ Sync complete — <strong>{syncResult.added}</strong> new tournaments stored,{' '}
              <strong>{syncResult.skipped}</strong> already stored, <strong>{syncResult.total}</strong> total
              {syncResult.failed > 0 && <span style={{ color: '#f59e0b' }}> · {syncResult.failed} failed</span>}
            </div>
          )}

          {error && (
            <div style={{ padding: '12px', backgroundColor: 'rgba(239,68,68,0.12)', color: '#f87171', borderRadius: '8px', fontSize: '14px', marginBottom: '16px', border: '1px solid rgba(239,68,68,0.2)' }}>
              ⚠️ {error}
            </div>
          )}

          {/* CueScore username input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', color: '#8b95a8', marginBottom: '6px' }}>
              CueScore public username
            </label>
            <input
              style={{ ...styles.input, width: '280px', maxWidth: '100%' }}
              value={cuescoreInput}
              onChange={e => setCuescoreInput(e.target.value)}
              placeholder="e.g. rgpick"
              disabled={syncing}
            />
            <div style={{ fontSize: '12px', color: '#5a6478', marginTop: '4px' }}>
              cuescore.com/<strong style={{ color: '#8b95a8' }}>{cuescoreInput || 'username'}</strong>/tournaments — no password needed
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleSyncHistory(false)}
              disabled={syncing || loading || !cuescoreInput.trim()}
              style={styles.btn(syncing || !cuescoreInput.trim() ? '#374151' : '#10b981')}
            >
              {syncing ? '⏳ Syncing from CueScore...' : historyMeta ? '🔄 Sync New Tournaments' : '🚀 Initial Sync (all history)'}
            </button>
            {historyMeta && (
              <button
                onClick={() => {
                  if (window.confirm(`Re-sync all ${historyMeta.totalStored} stored tournaments from CueScore to pick up any corrections? This will take several minutes.`)) {
                    handleSyncHistory(true);
                  }
                }}
                disabled={syncing || loading}
                style={styles.btn(syncing ? '#374151' : '#d97706')}
              >
                🔁 Full Re-sync
              </button>
            )}
          </div>

          {syncing && (
            <p style={{ color: '#8b95a8', fontSize: '13px', marginTop: '12px' }}>
              Fetching all tournament history — this may take several minutes on first run. You can leave this page open and come back.
            </p>
          )}
        </div>

        {/* Baseline Grade Seeding */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>🌱 Seed Baseline Grades</h2>
          <p style={{ color: '#8b95a8', marginBottom: '16px', fontSize: '14px' }}>
            Step 1b — Write the 2025 manually-curated grade list into the player grade store.
            Only adds grades where none exist — never overwrites manual edits. Run once after
            syncing history; re-run after adding new sync data.
          </p>

          {seedResult && (
            <div style={{
              padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
              backgroundColor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
              fontSize: '14px', color: '#10b981',
            }}>
              ✅ <strong>{seedResult.seeded}</strong> grades seeded ·{' '}
              <strong>{seedResult.skipped}</strong> already set ·{' '}
              <strong>{seedResult.matched}</strong> names matched in history
              {seedResult.unmatched.length > 0 && (
                <div style={{ marginTop: '8px', color: '#f59e0b', fontSize: '13px' }}>
                  ⚠️ {seedResult.unmatched.length} names not found in history:{' '}
                  {seedResult.unmatched.map(u => `${u.name} (${u.grade})`).join(', ')}
                </div>
              )}
              {seedResult.ambiguous.length > 0 && (
                <div style={{ marginTop: '6px', color: '#8b95a8', fontSize: '13px' }}>
                  ℹ️ {seedResult.ambiguous.length} ambiguous (same name, multiple grades — assign manually):{' '}
                  {seedResult.ambiguous.map(a => `${a.name}`).join(', ')}
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleSeedGrades}
            disabled={seeding || !historyMeta}
            style={styles.btn(seeding || !historyMeta ? '#374151' : '#8b5cf6')}
          >
            {seeding ? '⏳ Seeding...' : seedResult ? '♻️ Re-seed Baseline' : '🌱 Seed 2025 Baseline Grades'}
          </button>
          {!historyMeta && (
            <p style={{ color: '#5a6478', fontSize: '13px', marginTop: '8px' }}>
              Run a tournament sync first to populate the history store.
            </p>
          )}
        </div>

        {/* Grading Stats Panel */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>📊 Player Grading Stats</h2>
          <p style={{ color: '#8b95a8', marginBottom: '16px', fontSize: '14px' }}>
            Step 2 — Compute per-player stats from stored history. Fast — reads from Firebase only.
          </p>
          {lastGenerated && (
            <p style={{ color: '#5a6478', fontSize: '13px', marginBottom: '16px' }}>
              Stats computed: {lastGenerated.toLocaleString()} · {tournamentsProcessed} tournaments · {players.length} players
            </p>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => fetchGradingData()}
              disabled={loading}
              style={styles.btn(loading ? '#374151' : '#3b82f6')}
            >
              {loading ? '⏳ Computing...' : players.length === 0 ? '📈 Compute Grading Stats' : '♻️ Recompute Stats'}
            </button>

            {/* Sync All Player Histories — batches of 3, auto-advances until done */}
            {syncAllProgress && syncAllProgress.running ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', color: '#8b95a8' }}>
                  ⏳ {syncAllProgress.processed}
                  {syncAllProgress.total ? ` / ${syncAllProgress.total}` : ''} players
                  {syncAllProgress.added > 0 ? ` · ${syncAllProgress.added} tournaments added` : ''}
                </span>
                <button
                  onClick={() => { syncAllCancelRef.current = true; }}
                  style={styles.outlineBtn(true)}
                >
                  ✗ Stop
                </button>
              </div>
            ) : (
              <button
                onClick={handleSyncAll}
                disabled={loading || (syncAllProgress && syncAllProgress.running)}
                style={styles.outlineBtn()}
              >
                {syncAllProgress && !syncAllProgress.running
                  ? `✅ Done — ${syncAllProgress.added} tournaments added`
                  : '🔄 Sync All Player Histories'}
              </button>
            )}
          </div>
          {syncAllProgress && !syncAllProgress.running && syncAllProgress.processed > 0 && (
            <p style={{ color: '#5a6478', fontSize: '12px', marginTop: '6px' }}>
              Processed {syncAllProgress.processed} players · click ♻️ Recompute Stats to apply
            </p>
          )}
        </div>

        {/* Filters & Search */}
        {players.length > 0 && (
          <div style={styles.card}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                style={{ ...styles.input, flex: '1', minWidth: '160px' }}
                placeholder="🔍 Search player..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <select
                style={styles.select}
                value={filterTier}
                onChange={e => setFilterTier(e.target.value)}
              >
                <option value="All">All Tiers</option>
                {GRADE_TIERS.map(t => <option key={t} value={t}>Grade {t}</option>)}
                <option value="Open">Open / Unranked</option>
              </select>
              <select
                style={styles.select}
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value="winRate">Sort: Win %</option>
                <option value="matches">Sort: Matches</option>
                <option value="name">Sort: Name</option>
                <option value="grade">Sort: Assigned Grade</option>
              </select>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#8b95a8', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filterUngraded}
                  onChange={e => setFilterUngraded(e.target.checked)}
                  style={{ accentColor: '#3b82f6' }}
                />
                Ungraded only
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#8b95a8', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filterFlags}
                  onChange={e => setFilterFlags(e.target.checked)}
                  style={{ accentColor: '#f59e0b' }}
                />
                Flagged only
              </label>
            </div>
            <div style={{ marginTop: '10px', fontSize: '13px', color: '#5a6478' }}>
              Showing {displayPlayers.length} of {players.length} players ·{' '}
              {players.filter(p => savedGrades[p.playerId]).length} graded ·{' '}
              {players.filter(p => p.flags && p.flags.length > 0).length} flagged
            </div>
          </div>
        )}

        {/* Confirm Warning Modal */}
        {confirmWarning && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px',
          }}>
            <div style={{ backgroundColor: '#151c2e', borderRadius: '14px', padding: '28px', maxWidth: '480px', width: '100%', border: '1px solid #f59e0b' }}>
              <div style={{ fontSize: '20px', marginBottom: '12px' }}>⚠️ Grade Mismatch</div>
              <p style={{ color: '#f0f2f5', marginBottom: '8px', fontWeight: 'bold' }}>{confirmWarning.playerName}</p>
              <p style={{ color: '#8b95a8', marginBottom: '20px', fontSize: '14px' }}>{confirmWarning.warning}</p>
              <p style={{ color: '#8b95a8', marginBottom: '20px', fontSize: '14px' }}>
                You are assigning: <strong style={{ color: '#f0f2f5' }}>{confirmWarning.grade}</strong>
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => handleSaveGrade(confirmWarning.playerId, confirmWarning.grade, confirmWarning.notes, true)}
                  style={styles.btn('#f59e0b')}
                >
                  ✅ Yes, Save Anyway
                </button>
                <button onClick={() => setConfirmWarning(null)} style={styles.outlineBtn()}>
                  ✗ Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rebuttal modal — shown when organiser assigns a different tier than suggested */}
        {rebuttalModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '24px' }}>
            <div style={{ backgroundColor: '#151c2e', borderRadius: '14px', padding: '28px', maxWidth: '480px', width: '100%', border: '1px solid #2a3555' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>📝 Grade differs from suggestion</div>
              <p style={{ color: '#f0f2f5', marginBottom: '4px', fontWeight: 'bold' }}>{rebuttalModal.playerName}</p>
              <p style={{ color: '#8b95a8', fontSize: '14px', marginBottom: '16px' }}>
                You're assigning <strong style={{ color: '#f0f2f5' }}>{rebuttalModal.grade}</strong>, system suggested <strong style={{ color: rebuttalModal.suggestion && rebuttalModal.suggestion.rationale ? (rebuttalModal.suggestion.rationale.confidence === 'high' ? '#10b981' : rebuttalModal.suggestion.rationale.confidence === 'medium' ? '#f59e0b' : '#ef4444') : '#5a6478' }}>{rebuttalModal.suggestion && rebuttalModal.suggestion.grade}</strong>.
                Adding a note helps improve future suggestions — it's optional.
              </p>
              <textarea
                style={{ width: '100%', backgroundColor: '#1c2640', color: '#f0f2f5', border: '1px solid #2a3555', borderRadius: '8px', padding: '10px', fontSize: '14px', resize: 'vertical', minHeight: '70px', boxSizing: 'border-box' }}
                placeholder="e.g. Only plays in pub leagues, wins don't reflect real grade..."
                value={rebuttalNote}
                onChange={e => setRebuttalNote(e.target.value)}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button onClick={handleSaveFromRebuttal} style={styles.btn('#10b981')}>
                  💾 Save{rebuttalNote.trim() ? ' with note' : ' without note'}
                </button>
                <button onClick={() => setRebuttalModal(null)} style={styles.outlineBtn()}>
                  ✗ Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Player List */}
        {players.length > 0 && (
          <div style={styles.card}>
            {/* Collapsible header */}
            <div
              onClick={() => setPlayersExpanded(v => !v)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            >
              <h2 style={{ ...styles.cardTitle, marginBottom: 0 }}>
                🎱 Players ({displayPlayers.length})
                {!playersExpanded && <span style={{ fontSize: '13px', color: '#5a6478', fontWeight: 'normal', marginLeft: '10px' }}>Click to expand</span>}
              </h2>
              <span style={{ fontSize: '20px', color: '#5a6478' }}>{playersExpanded ? '▲' : '▼'}</span>
            </div>

            {playersExpanded && (
              <>
                {/* Pagination controls */}
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', color: '#5a6478' }}>
                    Page {currentPage} of {Math.max(1, Math.ceil(displayPlayers.length / pageSize))} · showing {Math.min(pageSize, displayPlayers.length - (currentPage - 1) * pageSize)} of {displayPlayers.length}
                  </span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginLeft: 'auto' }}>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={styles.outlineBtn(true)}>◀</button>
                    <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(displayPlayers.length / pageSize), p + 1))} disabled={currentPage >= Math.ceil(displayPlayers.length / pageSize)} style={styles.outlineBtn(true)}>▶</button>
                    <select
                      value={pageSize}
                      onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                      style={styles.select}
                    >
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                      <option value={99999}>All</option>
                    </select>
                  </div>
                </div>

                {displayPlayers.length === 0 ? (
                  <p style={{ color: '#8b95a8' }}>No players match your filters.</p>
                ) : (
                  displayPlayers.slice((currentPage - 1) * pageSize, currentPage * pageSize).map(player => {
                const saved = savedGrades[player.playerId];
                const isEditing = editingGrade && editingGrade.playerId === player.playerId;
                const isSavingThis = saving === player.playerId;
                const isExpanded = expandedPlayer === player.playerId;
                const isRationaleOpen = expandedRationale === player.playerId;
                const suggConf = player.suggestion && player.suggestion.rationale
                  ? player.suggestion.rationale.confidence : null;
                const suggBadgeColor = suggConf === 'high' ? '#10b981'
                  : suggConf === 'medium' ? '#f59e0b'
                  : suggConf === 'low' ? '#ef4444' : '#5a6478';

                return (
                  <div key={player.playerId} style={styles.playerRow}>
                    {/* Player header row */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '10px',
                      marginBottom: isEditing ? '16px' : '8px',
                    }}>
                      {/* Name + badges */}
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                          <a
                            href={`https://cuescore.com/player/${player.playerName.replace(/\s+/g, '+')}/${player.playerId}/tournaments`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: '16px', fontWeight: 'bold', color: '#f0f2f5', textDecoration: 'none' }}
                            onMouseEnter={e => { e.target.style.color = '#3b82f6'; }}
                            onMouseLeave={e => { e.target.style.color = '#f0f2f5'; }}
                          >{player.playerName}</a>
                          {saved && (
                            <span style={styles.badge(gradeColour(saved.grade))}>
                              {saved.grade}
                            </span>
                          )}
                          {player.suggestion && (
                            <span style={{
                              ...styles.badge(suggBadgeColor),
                              fontSize: '12px',
                              cursor: 'pointer',
                            }} onClick={() => setExpandedRationale(isRationaleOpen ? null : player.playerId)}
                            title="Click to see why this grade was suggested">
                              💡 {player.suggestion.grade}
                              {suggConf === 'low' && ' ⚠'}
                            </span>
                          )}
                          {player.flags && player.flags.map((flag, fi) => (
                            <span key={fi} style={styles.badge(flag.type === 'promote' ? '#10b981' : '#f59e0b')}>
                              {flag.type === 'promote' ? '⬆' : '⚠'} {flag.type === 'promote' ? 'Promote?' : 'Review'}
                            </span>
                          ))}
                        </div>

                        {/* Quick stats row */}
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={styles.statPill}>
                            🏆 {player.winRate}% wins
                          </span>
                          <span style={styles.statPill}>
                            ⚔️ {player.matchesWon}W–{player.matchesLost}L
                          </span>
                          <span style={styles.statPill}>
                            🏟️ {player.tournamentsEntered} events
                          </span>
                          <span style={styles.statPill}>
                            {finishEmoji(player.bestFinish)} {player.bestFinish}
                          </span>
                          {player.tournamentWins > 0 && (
                            <span style={{ ...styles.statPill, color: '#f59e0b' }}>
                              🎖 {player.tournamentWins} win{player.tournamentWins > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {/* Flag messages */}
                        {player.flags && player.flags.length > 0 && (
                          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {player.flags.map((flag, fi) => (
                              <div key={fi} style={{
                                fontSize: '12px',
                                color: flag.type === 'promote' ? '#10b981' : '#f59e0b',
                                display: 'flex', gap: '6px', alignItems: 'center',
                              }}>
                                <span>{flag.type === 'promote' ? '⬆️' : '⚠️'}</span>
                                <span>{flag.message}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action buttons: History | Sync | Why? | +Grade */}
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                        <button
                          onClick={() => setExpandedPlayer(isExpanded ? null : player.playerId)}
                          style={styles.outlineBtn(true)}
                        >
                          {isExpanded ? '▲ Hide' : '▼ History'}
                        </button>
                        <button
                          onClick={() => handleSyncPlayerHistory(player.playerId)}
                          disabled={syncingPlayers}
                          style={{ ...styles.outlineBtn(true), color: syncingPlayers ? '#5a6478' : '#8b5cf6', borderColor: '#8b5cf666' }}
                          title="Sync this player's graded tournaments from CueScore"
                        >
                          {syncingPlayers ? '⏳' : '🔄 Sync'}
                        </button>
                        {player.suggestion && (
                          <button
                            onClick={() => setExpandedRationale(isRationaleOpen ? null : player.playerId)}
                            style={{
                              ...styles.outlineBtn(true),
                              color: suggBadgeColor,
                              borderColor: suggBadgeColor + '66',
                            }}
                          >
                            {isRationaleOpen ? '▲ Why?' : '💡 Why?'}
                          </button>
                        )}
                        <button
                          onClick={() => setEditingGrade(
                            isEditing ? null : {
                              playerId: player.playerId,
                              grade: saved ? saved.grade : (player.suggestion ? player.suggestion.grade : 'Mid D'),
                              notes: saved ? (saved.notes || '') : '',
                            }
                          )}
                          style={styles.btn(isEditing ? '#374151' : '#3b82f6', true)}
                          disabled={isSavingThis}
                        >
                          {isSavingThis ? '⏳' : isEditing ? '✗ Cancel' : saved ? '✏️ Edit' : '+ Grade'}
                        </button>
                      </div>
                    </div>

                    {/* Inline grade editor */}
                    {isEditing && (
                      <div style={{
                        backgroundColor: '#0d1321',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '8px',
                        border: '1px solid #3b82f666',
                      }}>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div>
                            <div style={{ fontSize: '12px', color: '#8b95a8', marginBottom: '4px' }}>Grade</div>
                            <select
                              style={{ ...styles.select, minWidth: '140px' }}
                              value={editingGrade.grade}
                              onChange={e => setEditingGrade({ ...editingGrade, grade: e.target.value })}
                            >
                              {ALL_GRADES.map(g => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <div style={{ fontSize: '12px', color: '#8b95a8', marginBottom: '4px' }}>Notes (optional)</div>
                            <input
                              style={{ ...styles.input, width: '100%' }}
                              value={editingGrade.notes}
                              onChange={e => setEditingGrade({ ...editingGrade, notes: e.target.value })}
                              placeholder="e.g. Strong break builder, improving rapidly..."
                            />
                          </div>
                          <button
                            onClick={() => handleSaveGrade(editingGrade.playerId, editingGrade.grade, editingGrade.notes)}
                            style={styles.btn('#10b981')}
                          >
                            💾 Save Grade
                          </button>
                        </div>
                        {player.suggestion && editingGrade.grade !== player.suggestion.grade && (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#8b95a8' }}>
                            💡 Data suggests: <strong style={{ color: '#f0f2f5' }}>{player.suggestion.grade}</strong>
                            {' '}({player.winRate}% win rate across {player.matchesPlayed} matches)
                          </div>
                        )}
                        {saved && saved.notes && editingGrade.notes === saved.notes && (
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#5a6478' }}>
                            Previously: {saved.notes}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Rationale panel */}
                    {isRationaleOpen && player.suggestion && (
                      <div style={{ backgroundColor: '#0d1321', borderRadius: '8px', padding: '14px', marginTop: '8px', border: `1px solid ${suggBadgeColor}44` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            💡 Why {player.suggestion.grade}?
                          </span>
                          <span style={{ ...styles.badge(suggBadgeColor), fontSize: '11px' }}>
                            {(player.suggestion.rationale && player.suggestion.rationale.confidence) || '?'} confidence
                          </span>
                          {player.suggestion.rationale && player.suggestion.rationale.flags && player.suggestion.rationale.flags.map((f, fi) => {
                            const flagLabels = {
                              no_grade_context: '⚠️ No grade context',
                              suggestion_unreliable: '⚠️ Unreliable',
                              no_graded_events: '⚠️ No graded events',
                              grade_inferred_from_opponents: 'ℹ️ From opponents',
                              limited_data: 'ℹ️ Limited data',
                              opponent_evidence_borderline: 'ℹ️ Borderline evidence',
                            };
                            return flagLabels[f] ? (
                              <span key={fi} style={{ fontSize: '11px', color: f.startsWith('no_') || f === 'suggestion_unreliable' ? '#ef4444' : '#f59e0b' }}>
                                {flagLabels[f]}
                              </span>
                            ) : null;
                          })}
                        </div>
                        {player.suggestion.rationale && player.suggestion.rationale.evidence && player.suggestion.rationale.evidence.map((ev, ei) => (
                          <div key={ei} style={{ fontSize: '12px', color: '#8b95a8', padding: '4px 0', borderBottom: '1px solid #2a3555' }}>
                            <span style={{ color: ev.type === 'winrate_fallback' ? '#ef4444' : ev.type === 'opponent_grade' ? '#f59e0b' : '#10b981', marginRight: '6px' }}>
                              {ev.type === 'graded_event' ? '🏆' : ev.type === 'opponent_grade' ? '👥' : ev.type === 'grade_level_event' ? '📊' : '⚠️'}
                            </span>
                            {ev.description}
                          </div>
                        ))}
                        {saved && saved.rebuttal && (
                          <div style={{ marginTop: '10px', padding: '8px', backgroundColor: 'rgba(245,158,11,0.08)', borderRadius: '6px', border: '1px solid rgba(245,158,11,0.25)' }}>
                            <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 'bold', marginBottom: '4px' }}>📝 Organiser note</div>
                            <div style={{ fontSize: '12px', color: '#8b95a8' }}>{saved.rebuttal.note}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Expanded recent event history */}
                    {isExpanded && player.recentEvents && player.recentEvents.length > 0 && (
                      <div style={{
                        backgroundColor: '#0d1321',
                        borderRadius: '8px',
                        padding: '14px',
                        marginTop: '8px',
                        border: '1px solid #2a3555',
                      }}>
                        <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '10px', color: '#8b95a8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Recent Events
                        </div>
                        {player.recentEvents.map((ev, idx) => (
                          <div key={idx} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '8px 0', borderBottom: idx < player.recentEvents.length - 1 ? '1px solid #2a3555' : 'none',
                            flexWrap: 'wrap', gap: '6px',
                          }}>
                            <div style={{ flex: 1, minWidth: '150px' }}>
                              <div style={{ fontSize: '14px', marginBottom: '2px' }}>{ev.tournamentName}</div>
                              {ev.starttime && (
                                <div style={{ fontSize: '12px', color: '#5a6478' }}>
                                  {new Date(ev.starttime).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={styles.badge(GRADE_COLOURS[ev.eventGrade.tier] || '#374151')}>
                                {ev.eventGrade.level ? `${ev.eventGrade.level} ${ev.eventGrade.tier}` : ev.eventGrade.tier}
                              </span>
                              <span style={{ ...styles.statPill, fontSize: '12px' }}>
                                {ev.matchesWon}W–{ev.matchesPlayed - ev.matchesWon}L
                              </span>
                              <span style={{ ...styles.statPill, fontSize: '12px' }}>
                                {finishEmoji(ev.bestFinish)} {ev.bestFinish}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
                  })
                )}
              </>
            )}
          </div>
        )}

        {/* Empty state */}
        {!loading && players.length === 0 && (
          <div style={styles.card}>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
              <p style={{ color: '#8b95a8', marginBottom: '12px' }}>
                No grading stats computed yet.
              </p>
              <p style={{ color: '#5a6478', fontSize: '13px' }}>
                {!historyMeta
                  ? 'Run "Sync New Tournaments" first to pull your full history from CueScore into Firebase. Then click "Compute Grading Stats".'
                  : `${historyMeta.totalStored} tournaments are stored. Click "Compute Grading Stats" above to generate player data.`}
              </p>
            </div>
          </div>
        )}

        {/* Grade Reference Card */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>📋 Grade Reference</h2>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {GRADE_TIERS.map(tier => (
              <div key={tier} style={{
                flex: '1', minWidth: '140px',
                backgroundColor: '#0d1321',
                borderRadius: '10px',
                padding: '14px',
                border: `1px solid ${GRADE_COLOURS[tier]}44`,
              }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: GRADE_COLOURS[tier], marginBottom: '8px', fontFamily: "'Outfit', sans-serif" }}>
                  Grade {tier}
                </div>
                <div style={{ fontSize: '12px', color: '#5a6478', lineHeight: '1.5' }}>
                  {tier === 'A' && 'Win rate >70%, multiple wins or finals'}
                  {tier === 'B' && 'Win rate 50–70%, semi/final appearances'}
                  {tier === 'C' && 'Win rate 35–50%, reaches later rounds'}
                  {tier === 'D' && 'Win rate 20–35%, mostly group exits'}
                  {tier === 'E' && 'Win rate <20%, new or developing'}
                </div>
                <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {GRADE_LEVELS.map(l => (
                    <span key={l} style={{ ...styles.badge(GRADE_COLOURS[tier]), fontSize: '11px' }}>{l}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlayerGrading;
